import { ui } from '@procuros/datachecks';

import { logError } from 'services/logging/logging';
import { track } from 'support/helpers/analytics/analytics';
import { GenericError } from 'support/helpers/errors/errors';
import { ProcessSpecificationDTO } from 'support/types/dtos';

import datachecksWorker from './datachecksWorkerBootstrap?worker&url';
import {
  GetCanonicalAndErrorsResult,
  GetCanonicalResult,
  GetUiDetailsResult,
  isWorkerErrorMessage,
  WorkerErrorMessage,
} from './types';

// ============================================================================
// TYPES
// ============================================================================

type Task = {
  action: string;
  params: Record<string, unknown>;
  promise: Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  isProcessing: boolean;
};

type TaskContext = {
  action: string;
  queueSize?: number;
  paramsPreview?: string;
};

// ============================================================================
// WORKER MANAGER
// ============================================================================

class WorkerManager {
  private worker: Worker | null = null;
  private workerUrl: string | null = null;
  private workerCacheBust: number | null = null;
  private cacheBustDisabled = false;
  private taskQueue: Array<Task> = [];
  private isProcessing = false;

  // --------------------------------------------------------------------------
  // Worker Lifecycle
  // --------------------------------------------------------------------------

  private createWorker(): Worker {
    if (!this.worker) {
      const workerUrl = this.buildWorkerUrl();
      this.workerUrl = workerUrl;
      this.worker = new Worker(workerUrl, { type: 'module' });
      this.worker.onmessage = this.handleMessage.bind(this);
      this.worker.onerror = this.handleFatalError.bind(this);
      this.worker.onmessageerror = this.handleMessageError.bind(this);
    }
    return this.worker;
  }

  /**
   * Terminates current worker and resets state.
   * A new worker will be created on the next task.
   * Note: Does NOT reject pending tasks - they will run on the new worker.
   */
  private recreateWorker(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
      this.workerUrl = null;
    }
    this.isProcessing = false;
  }

  public terminateWorker(): void {
    this.recreateWorker();
    this.taskQueue = [];
  }

  // --------------------------------------------------------------------------
  // Message Handlers
  // --------------------------------------------------------------------------

  private handleMessage(event: MessageEvent): void {
    if (isWorkerErrorMessage(event.data)) {
      this.handleWorkerError(event.data);
    } else {
      this.handleWorkerSuccess(event.data);
    }
    this.processNextTask();
  }

  private handleWorkerError(workerError: WorkerErrorMessage): void {
    const currentTask = this.taskQueue.shift();

    console.log('DataChecks Worker Error:', workerError);
    const error = new GenericError(workerError.message, {
      stack: workerError.stack,
      source: 'worker',
      action: workerError.action,
      errorName: workerError.name,
      isGlobalError: workerError.isGlobalError ?? false,
      diagnostics: workerError.diagnostics ?? {},
      taskContext: this.buildTaskContext(currentTask),
      raw: this.safeStringify(workerError, 1000),
    });

    logError(error);
    currentTask?.reject(error);

    // Recreate worker after global errors to ensure clean state
    if (workerError.isGlobalError) {
      this.recreateWorker();
    }
  }

  private handleWorkerSuccess(message: { action: string; result: unknown }): void {
    const { action, result } = message;

    const taskIndex = this.taskQueue.findIndex((t) => t.action === action && t.isProcessing);

    if (taskIndex === -1) {
      console.warn(`[WorkerManager] No matching task found for action: ${action}`);
      return;
    }

    const [matchedTask] = this.taskQueue.splice(taskIndex, 1);
    matchedTask.resolve(result);
  }

  private handleMessageError(event: MessageEvent): void {
    const error = new GenericError('Worker message serialization error', {
      messageError: true,
      eventType: event.type,
      eventData: this.safeStringify(event.data, 500),
      taskContext: this.getQueueContext(),
    });

    logError(error);
    this.rejectCurrentTask(error);
    this.processNextTask();
  }

  /**
   * Handles fatal worker errors (script load failures, etc.)
   * Most errors should be caught by worker's internal handlers.
   */
  private handleFatalError(event: ErrorEvent): void {
    const taskContext = this.getQueueContext();
    const workerState = this.isProcessing ? 'processing' : 'idle';
    const workerUrl = this.workerUrl ?? datachecksWorker;
    const error = new GenericError(event.message || 'Worker fatal error', {
      workerError: true,
      isFatal: true,
      errorType: event.type,
      eventMessage: event.message,
      filename: event.filename,
      lineNumber: event.lineno,
      columnNumber: event.colno,
      eventErrorName: event.error?.name,
      eventErrorMessage: event.error?.message,
      eventErrorStack: event.error?.stack,
      timeStamp: event.timeStamp,
      workerSource: datachecksWorker,
      workerUrl,
      cacheBustActive: Boolean(this.workerCacheBust),
      cacheBustDisabled: this.cacheBustDisabled,
      eventSnapshot: this.buildErrorEventSnapshot(event),
      taskContext,
      workerState,
    });

    this.probeWorkerAsset(
      workerUrl,
      {
        taskContext,
        workerState,
        errorType: event.type,
        errorMessage: event.message,
      },
      error,
    );
    this.markWorkerStale();
    this.disableCacheBustAfterFailure(workerUrl);

    this.rejectCurrentTask(error);
    this.recreateWorker();
    this.processNextTask();
  }

  // --------------------------------------------------------------------------
  // Task Queue Management
  // --------------------------------------------------------------------------

  public runTask(action: string, params: Record<string, unknown>): Promise<unknown> {
    try {
      // Reuse existing unstarted task with same action
      const existingTask = this.taskQueue.find((t) => t.action === action && !t.isProcessing);
      if (existingTask) {
        existingTask.params = params;
        return existingTask.promise;
      }

      // Create new task
      const task = this.createTask(action, params);
      this.taskQueue.push(task);
      this.processNextTask();

      return task.promise;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logError(
        new GenericError('Error queueing task', {
          message: error.message,
          taskAction: action,
          error,
        }),
      );
      return Promise.reject(error);
    }
  }

  private createTask(action: string, params: Record<string, unknown>): Task {
    let resolve!: (value: unknown) => void;
    let reject!: (reason?: unknown) => void;

    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });

    return { action, params, promise, resolve, reject, isProcessing: false };
  }

  private processNextTask(): void {
    if (this.taskQueue.length === 0 || this.isProcessing) {
      this.isProcessing = false;
      return;
    }
    const nextTask = this.taskQueue[0];
    this.isProcessing = true;
    nextTask.isProcessing = true;

    try {
      const worker = this.createWorker();
      worker.postMessage({ action: nextTask.action, ...nextTask.params });
    } catch (err) {
      this.handleTaskInitError(nextTask, err);
    }
  }

  private handleTaskInitError(task: Task, err: unknown): void {
    const error = err instanceof Error ? err : new Error(String(err));

    logError(
      new GenericError('Failed to initiate worker task', {
        message: error.message,
        taskAction: task.action,
        error,
      }),
    );

    this.taskQueue.shift();
    task.reject(error);
    this.isProcessing = false;
    this.processNextTask();
  }

  private rejectCurrentTask(error: Error): void {
    const currentTask = this.taskQueue.shift();
    currentTask?.reject(error);
  }

  private markWorkerStale(): void {
    if (this.cacheBustDisabled) {
      return;
    }
    this.workerCacheBust = Date.now();
  }

  private buildWorkerUrl(): string {
    if (!this.workerCacheBust || this.cacheBustDisabled) {
      return datachecksWorker;
    }
    const separator = datachecksWorker.includes('?') ? '&' : '?';
    return `${datachecksWorker}${separator}v=${this.workerCacheBust}`;
  }

  private disableCacheBustAfterFailure(workerUrl: string): void {
    if (!this.workerCacheBust) {
      return;
    }
    if (!workerUrl.includes('v=')) {
      return;
    }
    this.workerCacheBust = null;
    this.cacheBustDisabled = true;
  }

  private probeWorkerAsset(
    workerUrl: string,
    context: {
      taskContext: TaskContext | string;
      workerState: string;
      errorType: string;
      errorMessage: string;
    },
    fatalError?: GenericError,
  ): void {
    if (!workerUrl || typeof fetch !== 'function') {
      return;
    }

    void fetch(workerUrl, { method: 'HEAD', cache: 'no-store' })
      .then((response) => {
        if (response.status === 405) {
          this.probeWorkerAssetWithGet(workerUrl, context, fatalError);
          return;
        }
        this.logWorkerProbeResult('Worker asset probe (HEAD)', response, workerUrl, context, fatalError);
      })
      .catch((probeError) => {
        const error = probeError instanceof Error ? probeError : new Error(String(probeError));
        if (fatalError) {
          logError(fatalError);
        }
        logError(
          new GenericError('Worker asset probe failed', {
            ...context,
            workerUrl,
            probeErrorMessage: error.message,
          }),
        );
      });
  }

  private probeWorkerAssetWithGet(
    workerUrl: string,
    context: {
      taskContext: TaskContext | string;
      workerState: string;
      errorType: string;
      errorMessage: string;
    },
    fatalError?: GenericError,
  ): void {
    void fetch(workerUrl, {
      method: 'GET',
      headers: { Range: 'bytes=0-200' },
      cache: 'no-store',
    })
      .then((response) => {
        this.logWorkerProbeResult('Worker asset probe (GET)', response, workerUrl, context, fatalError);
      })
      .catch((probeError) => {
        const error = probeError instanceof Error ? probeError : new Error(String(probeError));
        if (fatalError) {
          logError(fatalError);
        }
        logError(
          new GenericError('Worker asset probe failed (GET)', {
            ...context,
            workerUrl,
            probeErrorMessage: error.message,
          }),
        );
      });
  }

  private logWorkerProbeResult(
    message: string,
    response: Response,
    workerUrl: string,
    context: {
      taskContext: TaskContext | string;
      workerState: string;
      errorType: string;
      errorMessage: string;
    },
    fatalError?: GenericError,
  ): void {
    const contentType = response.headers.get('content-type');
    const shouldLog = this.shouldLogWorkerProbe(contentType);
    if (shouldLog) {
      if (fatalError) {
        logError(fatalError);
      }
      logError(
        new GenericError(message, {
          ...context,
          workerUrl,
          status: response.status,
          ok: response.ok,
          redirected: response.redirected,
          responseType: response.type,
          finalUrl: response.url,
          contentType,
          cacheControl: response.headers.get('cache-control'),
          age: response.headers.get('age'),
          via: response.headers.get('via'),
        }),
      );
    }
    this.maybeHardReloadForHtmlWorker({
      contentType,
      workerUrl,
      finalUrl: response.url,
      status: response.status,
    });
  }

  // --------------------------------------------------------------------------
  // Utilities
  // --------------------------------------------------------------------------

  private buildTaskContext(task?: Task): TaskContext | string {
    if (!task) {
      return 'no_task_in_queue';
    }

    return {
      action: task.action,
      paramsPreview: typeof task.params === 'object' ? Object.keys(task.params).join(', ') : String(task.params),
    };
  }

  private getQueueContext(): TaskContext | string {
    if (this.taskQueue.length === 0) {
      return 'no_tasks_in_queue';
    }

    return {
      action: this.taskQueue[0].action,
      queueSize: this.taskQueue.length,
    };
  }

  private safeStringify(value: unknown, maxLength: number): string {
    try {
      const str = typeof value === 'object' ? JSON.stringify(value) : String(value);
      return str.length > maxLength ? `${str.substring(0, maxLength)}...` : str;
    } catch {
      return '[Unable to stringify]';
    }
  }

  private hasReloadedForHtmlWorker(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const reloadKey = 'datachecks_worker_html_reload';
    try {
      return sessionStorage.getItem(reloadKey) === '1';
    } catch {
      return false;
    }
  }

  private shouldLogWorkerProbe(contentType: string | null): boolean {
    if (contentType && contentType.includes('text/html')) {
      return this.hasReloadedForHtmlWorker();
    }
    return true;
  }

  private maybeHardReloadForHtmlWorker(details: {
    contentType: string | null;
    workerUrl: string;
    finalUrl: string;
    status: number;
  }): void {
    if (!details.contentType || !details.contentType.includes('text/html')) {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }

    const reloadKey = 'datachecks_worker_html_reload';
    try {
      if (sessionStorage.getItem(reloadKey) === '1') {
        return;
      }
      sessionStorage.setItem(reloadKey, '1');
    } catch {
      // Ignore storage errors and continue with reload.
    }

    const logPayload = {
      contentType: details.contentType,
      workerUrl: details.workerUrl,
      finalUrl: details.finalUrl,
      status: details.status,
      reason: 'worker_asset_returned_html',
    };
    console.log('[Datachecks] Forcing reload due to invalid worker asset response', logPayload);
    try {
      track('datachecks_worker_hard_reload', logPayload);
    } catch {
      // Ignore analytics failures during recovery.
    }

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('dc_worker_reload', String(Date.now()));
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  }

  private buildErrorEventSnapshot(event: ErrorEvent): Record<string, unknown> {
    return {
      type: event.type,
      message: event.message,
      filename: event.filename,
      lineNumber: event.lineno,
      columnNumber: event.colno,
      timeStamp: event.timeStamp,
      isTrusted: event.isTrusted,
      targetType: this.getEventTargetType(event.target),
      currentTargetType: this.getEventTargetType(event.currentTarget),
      targetString: this.safeStringifyEventTarget(event.target),
      currentTargetString: this.safeStringifyEventTarget(event.currentTarget),
      eventErrorName: event.error?.name,
      eventErrorMessage: event.error?.message,
    };
  }

  private getEventTargetType(target: EventTarget | null | undefined): string | undefined {
    if (!target || typeof target !== 'object') {
      return undefined;
    }
    return 'constructor' in target && (target as { constructor?: { name?: string } }).constructor
      ? (target as { constructor?: { name?: string } }).constructor?.name
      : undefined;
  }

  private safeStringifyEventTarget(target: EventTarget | null | undefined): string | undefined {
    if (!target) {
      return undefined;
    }
    try {
      return String(target);
    } catch {
      return '[Unable to stringify]';
    }
  }
}

// ============================================================================
// SINGLETON INSTANCE & PUBLIC API
// ============================================================================

const workerManager = new WorkerManager();

export const getCanonical = (
  processSpecificationFields: ProcessSpecificationDTO['fields'],
  data: unknown,
): Promise<GetCanonicalResult> => {
  return workerManager.runTask('getCanonical', {
    processSpecification: { fields: processSpecificationFields },
    data,
  }) as Promise<GetCanonicalResult>;
};

export const getCanonicalAndErrors = (
  processSpecificationFields: ProcessSpecificationDTO['fields'],
  data: unknown,
): Promise<GetCanonicalAndErrorsResult> => {
  return workerManager.runTask('getCanonicalAndErrors', {
    processSpecification: { fields: processSpecificationFields },
    data,
  }) as Promise<GetCanonicalAndErrorsResult>;
};

export const getUiDetails = (
  processSpecification: Pick<ProcessSpecificationDTO, 'data' | 'fields'>,
): Promise<GetUiDetailsResult> => {
  return workerManager.runTask('getUiDetails', { processSpecification }) as Promise<GetUiDetailsResult>;
};

export const terminateDataChecksWorker = (): void => {
  workerManager.terminateWorker();
};

// ============================================================================
// UI UTILITIES
// ============================================================================

export const applyRowMasksToData = (
  data: Record<string, unknown> | null | undefined,
  uiConfig: Array<ui.Section>,
): Record<string, unknown> | null | undefined => {
  if (!data) {
    return data;
  }

  const filteredData = { ...data };

  for (const section of uiConfig) {
    if ('row_mask' in section && data[section.type]) {
      const sectionWithMask = section as ui.Section & { row_mask: Record<string, Array<string>> };
      filteredData[section.type] = applyRowMask(data[section.type] as Array<Record<string, string>>, sectionWithMask);
    }
  }

  return filteredData;
};

const applyRowMask = (
  fields: Array<Record<string, string>>,
  config: ui.Section & { row_mask: Record<string, Array<string>> },
): Array<Record<string, string>> => {
  const rowMaskKey = Object.keys(config.row_mask)[0];
  const rowMaskValues = config.row_mask[rowMaskKey];

  return rowMaskValues.map((value) => {
    const matchedField = fields.find((field) => field[rowMaskKey] === value);
    return matchedField ?? { [rowMaskKey]: value };
  });
};
